import { Spreadsheet, Worksheet } from "@jspreadsheet-ce/react"
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from "react"
import { CustomCellEditor, CustomCellType, updateCellView } from "./VariableCell"
import "jsuites/dist/jsuites.css";
import "jspreadsheet-ce/dist/jspreadsheet.css";
import { VariablePopover } from "./VariablePopover/VariablePopover"
import { UnitStates } from "../FlowSheetEditor/components/UnitSelect/UnitSelect"
import { EventMap, useEditorEventBusInstance } from "../FlowSheetEditor/events/useEventBusInstance"
import isObject from 'lodash/isObject'
import isEqual from 'lodash/isEqual'
import { ChangeType } from "@/types/common"
import { useDispatch, useSelector } from "react-redux"
import { addManualInputThunk } from "@/store/flowsheet/actions"
import { FlowsheetContext } from "../FlowSheetEditor/FlowsheetProvider"
import { getCoordinate } from "./common"
import { LineRecord, NodeState, SetOperation } from "../FlowSheetEditor/services/modal"
import { IUnitData } from "@/services/project/modal"
import { getFlowSimulationSelector } from "@/store/flowsheet/flowsheet.slice"

interface IEditCell {
  cell: HTMLTableCellElement
  x: number
  y: number
  value: any
}

interface NewSpreadsheetProps {
  units: {nodes: NodeState[], lines: LineRecord[], operations: SetOperation[]},
  onChange: (autoUpdate?: boolean) => void
  data: any
  simData?: IUnitData
}

export const NewSpreadsheet = forwardRef((props: NewSpreadsheetProps, ref) => {
  const {units, data: initData, simData, onChange} = props
  const spreadsheet = useRef<jspreadsheet.WorksheetInstance[]>();
  const [data] = useState<any>(initData ?? [])
  const [editCell, setEditCell] = useState<IEditCell | null>(null)
  const eventBus = useEditorEventBusInstance()
  const dispatch = useDispatch()
  const flowSim = useSelector(getFlowSimulationSelector)

  const updateData = useCallback((val: any, x: number, y: number) => {
    const tableInstance = spreadsheet.current?.[0]
    if (!tableInstance) {
      return;
    }
    tableInstance.setValueFromCoords(x, y, val, true)
  }, [])

  useEffect(() => {
    if (!simData) return;

    const vars = simData.vars?.CELLS || {}
    Object.keys(vars).forEach((key) => {
      const {x, y} = getCoordinate(key)
      const currentData = data[y][x]
      const varVal = vars[key]
      if (!isObject(currentData)) {
        return;
      }
      if (isEqual(varVal, currentData?.v)) {
        return;
      }
      const sheetInstance = spreadsheet.current?.[0]
      if (!sheetInstance) return;
      const cell = sheetInstance.getCellFromCoords(x,y)
      const newCellVal = {...currentData, v: varVal}
      updateCellView(cell, newCellVal)
      updateData(newCellVal, x, y)
    })
  }, [simData, updateData])

  const columns = [
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
    {type: CustomCellEditor()},
  ]

  useImperativeHandle(
    ref,
    () => ({
      spreadInstance: spreadsheet.current,
      getData: () => {
        const tableInstance = spreadsheet.current?.[0]
        if (!tableInstance) throw new Error("Error");
        
        return tableInstance.getData();
      },
    }),
    [spreadsheet.current]
  )

  useEffect(() => {
    const handler = ({payload, type}: {type: ChangeType, payload: any}) => {
      if (type === ChangeType.Change && payload.t === CustomCellType.Variable && isObject(payload.v)) {
        if (flowSim) {
          const {unitId, attrCode, v} = payload
          dispatch(addManualInputThunk({
            data: {dvars: [{unitId: unitId, attribute: attrCode, value: v.val}]},
            params: {sId: flowSim.id}
          }))
        }
      } else if (type === ChangeType.Delete) {
        
      }
      onChange?.()
    }
    eventBus.on(EventMap.spreadCellChange, handler)

    return () => {
      eventBus.off(EventMap.spreadCellChange, handler)
    }
  }, [setEditCell, flowSim])

  const contextMenu = (instance: jspreadsheet.WorksheetInstance, x, y, e, items, section) => {
    x = Number(x)
    y = Number(y)
    const cell = instance.getCellFromCoords(x,y)
    const cellData = data[y]?.[x]
    if (!isObject(cellData)) {
      items.push({
        title: 'Link Equipment variable.',
        onclick: function() {
          setEditCell({cell, x, y, value: data[y]?.[x]})
        }
      });

      items.push({
        title: 'Set cell to formula.',
        onclick: function() {
          const value = data[y]?.[x];
          let newValue = {v: value, t: CustomCellType.Formula}
          if (isObject(value)) {
            newValue = {...value, t: CustomCellType.Formula}
          }
          updateData(newValue, x, y)
          updateCellView(cell, newValue)
          eventBus.trigger(EventMap.spreadCellChange, {type: ChangeType.Change, payload: newValue})
        }
      });
    } else {
      if ([CustomCellType.Variable, CustomCellType.VariableFormula].includes(cellData.t)) {
        items.push({
          title: 'Change Equipment variable.',
          onclick: function() {
            setEditCell({cell, x, y, value: cellData})
          }
        });
      }

      if (cellData.t === CustomCellType.Formula) {
        items.push({
          title: 'Link Equipment variable.',
          onclick: function() {
            setEditCell({cell, x, y, value: data[y]?.[x]})
          }
        });
      }
      
      items.push({
        title: 'Set to normal cell.',
        onclick: function() {
          const cellVal = isObject(cellData.v) ? cellData.v.val : cellData.v
          updateData(cellVal, x, y)
          updateCellView(cell, cellVal)
          eventBus.trigger(EventMap.spreadCellChange, {type: ChangeType.Delete, payload: cellData})
        }
      });
    }
    
    return items;
  }

  const toolbar = (toolbar) => {
    const saveItem = toolbar.items[2]
    toolbar.items = toolbar.items.slice(4)
    saveItem.onclick = () => {
      onChange(true)
    }
    toolbar.items = [saveItem, ...toolbar.items]
    return toolbar;
  }

  const setUnitCell = (val) => {
    const oldCellVal: any = spreadsheet.current![0].getValueFromCoords(editCell!.x, editCell!.y)
    const cellVal = {
      ...oldCellVal ?? {},
      ...val,
      t: CustomCellType.Variable,
      v: '-',
    }
    if (oldCellVal && isObject(oldCellVal) && ([CustomCellType.VariableFormula, CustomCellType.Formula].includes(oldCellVal.t))) {
      cellVal.t = CustomCellType.VariableFormula
      cellVal.v = oldCellVal.v
    }
    
    updateCellView(editCell!.cell, cellVal)
    updateData(cellVal, editCell!.x, editCell!.y)
    setEditCell(null)
    eventBus.trigger(EventMap.spreadCellChange, {type: ChangeType.Change, payload: cellVal})
  }

  const hidePopover = useCallback(() => {
    setEditCell(null)
  }, [setEditCell])

  return <div style={{width: '100%', height: '100%'}}>
    <Spreadsheet ref={spreadsheet} tabs={true} toolbar={toolbar} contextMenu={contextMenu}>
      <Worksheet columns={columns} data={data} />
    </Spreadsheet>
    {editCell && <VariablePopover
      units={units as UnitStates}
      cell={editCell.cell}
      value={editCell.value}
      onCancel={hidePopover}
      onChange={setUnitCell}
    />}
  </div>
})
